import React, { useEffect, useState } from 'react';
import { PageWithNavigation } from '@/src/components';
import { ROUTE_ANYTIME_ELIGIBILITY, ROUTE_PLAN } from '@/src/constants/routes';
import { useHistory } from 'react-router-dom-v5';
import {
  FullWidthContentWrapper,
  HeaderRow,
  MainContentCard,
  TabButton,
  TabButtonGroup,
} from '../ProductSelection/ProductSelection.styled';
import {
  ContentGrid,
  Col,
  Row,
  Button,
  TextLink,
  Margin,
  P,
  Strong,
} from '@ovotech/element-v5';
import {
  showMultipleEvsBadge,
  PlanDetails,
  BoltonProductType,
  useSwitchPlanMutation,
  mapIncompatibilityToSwitchFallout,
  useChargeAnytimeProducts,
  BoltonPlanStatus,
  EV,
  getPlanNameFromBolton,
  useChargeAnytimeContext,
  isSelectedPlanMonthlyPlan,
  isSelectedPlanPaygPlan,
} from '@monovo/ev-anytime';
import ChargeAnytimePlans from '@/src/pages/Offers/components/cards/ChargeAnytimePlans/ChargeAnytimePlans';
import { useChargeAnytimeBoltonState } from '@/src/pages/AnytimeEligibilityV2/hooks/useChargeAnytimeBoltonState';
import { Loading } from '@/src/pages/AnytimeEligibilityV2/components/Loading';
import {
  useAccountContext,
  useAnalytics,
  useCustomerContext,
  Event,
} from '@ovotech/ui-tools';
import { fetchFn } from '@/src/pages/AnytimeEligibilityV2/services/chargeAnytimeAPI';
import {
  MonthlyPlanModal,
  PayAsYouGoModal, // Correct import is here
} from '../PreEligibilityProducts/modals';
import { useModalState } from '../PreEligibilityProducts/hooks';
import { TechnicalGlitchPage } from '@/src/pages/AnytimeEligibilityV2/pages/TechnicalGlitchPage/TechnicalGlitchPage';
import styled from 'styled-components';
import { Media } from '@/src/components/Media';
import { breakpoints } from '@/src/utils/mediaQuery';
import Footnote from '../../components/Footnote';
import { ProductType } from '../../components/PlanSelector';
import { PageHeaderV2 } from '../../components/PageHeaderV2';

const StyledTextLink = styled(TextLink)`
  font-weight: 450;
`;

const StyledContentGrid = styled(ContentGrid)`
  padding-inline: 0;
`;

const HorizontalScrollContainer = styled.div`
  display: flex;
  align-items: flex-start;
  overflow-x: auto;
  gap: 14px;
`;

export const ChargeAnytimeSwitch = () => {
  const { dispatch: analyticsDispatch } = useAnalytics();

  const history = useHistory();
  const [activeTab, setActiveTab] = useState('monthly');
  const { accountId } = useAccountContext();
  const { customerId } = useCustomerContext();

  const {
    showMonthlyModal,
    monthlyModalTab,
    monthlyModalStep,
    openMonthlyModal,
    closeMonthlyModal,
    handleTabSwitch,
    handleNextStep,
    handlePrevStep,
  } = useModalState();

  const [showPaygModal, setShowPaygModal] = useState(false);
  const openPaygModal = () => setShowPaygModal(true);
  const closePaygModal = () => {
    setShowPaygModal(false);
    analyticsDispatch({
      type: 'click',
      name: EV.ELIGIBILITY.CHARGE_ANYTIME_SWITCH_SCREEN_DETAILS_CLOSE_CLICKED,
      properties: {
        detailsView: 'Pay as you go',
        pathname: '/anytime/switch',
        journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
        screen: EV.SCREENS.MAKE_A_SWITCH,
      },
    });
  };

  const { boltonPlan, eligibleSwitchProducts } = useChargeAnytimeBoltonState();

  const switchMutation = useSwitchPlanMutation({
    customerId: customerId!,
    accountId: accountId!,
    chargeAnytimeApiFetch: fetchFn,
  });

  // This will be removed when switching logic chnages to allow customers
  // To switch when in a switching state
  useEffect(() => {
    if (
      boltonPlan?.status === BoltonPlanStatus.Switching &&
      !switchMutation.isLoading
    ) {
      history.push(ROUTE_PLAN);
    }
  }, [boltonPlan, history, switchMutation.isLoading]);

  const {
    data: products,
    isLoading,
    isError,
  } = useChargeAnytimeProducts('all');

  useEffect(() => {
    if (boltonPlan && products) {
      analyticsDispatch({
        type: 'view',
        name: EV.ELIGIBILITY.CHARGE_ANYTIME_SWITCH_SCREEN_VIEWED,
        properties: {
          pera: null,
          currentAddOnName: getPlanNameFromBolton(boltonPlan.current, products),
          currentAddOnState: boltonPlan.current?.status || null,
          pathname: '/anytime/switch',
          journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
          screen: EV.SCREENS.MAKE_A_SWITCH,
        },
      });
    }
  }, [boltonPlan]);

  const allProducts = products ?? [];

  const eligibleProducts =
    eligibleSwitchProducts && eligibleSwitchProducts.length > 0
      ? allProducts.filter(
          p =>
            eligibleSwitchProducts.includes(p.boltonProductType) &&
            !(
              p.productType === 'partnership' &&
              p.boltonProductType === BoltonProductType.PAYD
            ),
        )
      : allProducts.filter(
          (p: PlanDetails) =>
            p.productType === 'monthly' || p.productType === 'payg',
        );

  const monthlyPlans = eligibleProducts.filter((plan: PlanDetails) =>
    isSelectedPlanMonthlyPlan(plan.id),
  );
  const paygPlans = eligibleProducts.filter((plan: PlanDetails) =>
    isSelectedPlanPaygPlan(plan.id),
  );

  const paygPlan = paygPlans.find((plan: PlanDetails) => plan.id === 'payg');

  const hasPayg = boltonPlan?.current?.productType === BoltonProductType.PAYD;
  const isOemMonthlyPlan =
    monthlyPlans.length > 0 &&
    monthlyPlans.every(p => p.id === 'paym-oem-tier-one');
  const showTabs = monthlyPlans.length > 0 && paygPlans.length > 0 && !hasPayg;

  // When tabs are hidden there is only one product type to show — derive which
  // one directly rather than relying on state, so the correct value is always
  // used from the first render without a useEffect / extra re-render.
  const effectiveTab: ProductType = showTabs
    ? (activeTab as ProductType)
    : paygPlans.length > 0 && !hasPayg
    ? 'payg'
    : 'monthly';

  const handleSelectPlan = (planId: string) => {
    const futureAddOn =
      monthlyPlans.find(plan => plan.id === planId) ||
      paygPlans.find(plan => plan.id === planId);

    analyticsDispatch({
      type: 'submitted' as Event['type'],
      name: EV.ELIGIBILITY.CHARGE_ANYTIME_SWITCH_SCREEN_NEXT_CLICKED,
      properties: {
        futureAddOnName: futureAddOn?.title || null,
        pathname: '/anytime/switch',
        journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
        screen: EV.SCREENS.MAKE_A_SWITCH,
      },
    });

    switchMutation.mutate(
      { planId: planId as PlanDetails['id'] },
      {
        onSuccess: () => {
          history.push(ROUTE_ANYTIME_ELIGIBILITY.SWITCH_CONFIRMATION);
        },
        onError: error => {
          if (error.response?.status === 422) {
            const reasonsForIncompatibility =
              error.response.data?.reasonsForIncompatibility;
            const mappedFalloutReason = mapIncompatibilityToSwitchFallout(
              reasonsForIncompatibility,
            );
            history.push(
              `${ROUTE_ANYTIME_ELIGIBILITY.SWITCH_FALLOUT}?reason=${mappedFalloutReason}`,
            );
          } else {
            history.push(
              `${ROUTE_ANYTIME_ELIGIBILITY.SWITCH_FALLOUT}?reason=GENERAL_ERROR`,
            );
          }
        },
      },
    );
  };

  const handleTabChange = (tab: ProductType) => {
    setActiveTab(tab);
    if (tab !== activeTab) {
      analyticsDispatch({
        type: 'click',
        name: EV.ELIGIBILITY.CHARGE_ANYTIME_SWITCH_SCREEN_TOGGLE_CLICKED,
        properties: {
          selectedToggle: tab === 'monthly' ? 'Pay monthly' : 'Pay as you go',
          pathname: '/anytime/switch',
          journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
          screen: EV.SCREENS.MAKE_A_SWITCH,
        },
      });
    }
  };

  const handleBack = () => {
    if (boltonPlan && products) {
      analyticsDispatch({
        type: 'click',
        name: EV.ELIGIBILITY.CHARGE_ANYTIME_SWITCH_SCREEN_BACK_CLICKED,
        properties: {
          currentAddOnName: getPlanNameFromBolton(boltonPlan.current, products),
          currentAddOnState: boltonPlan.current?.status || null,
          pathname: '/anytime/switch',
          journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
          screen: EV.SCREENS.MAKE_A_SWITCH,
        },
      });
    }

    history.push(ROUTE_PLAN);
  };

  const { showKwhVariant } = useChargeAnytimeContext();
  const shouldShowFootnote = !(effectiveTab === 'payg' && showKwhVariant);

  const renderPlanGrid = (plans: PlanDetails[]) => {
    const filteredPlans = plans.filter(
      (plan: PlanDetails) =>
        plan.boltonProductType !== boltonPlan?.current?.productType,
    );

    return (
      <FullWidthContentWrapper>
        <StyledContentGrid>
          <Media query={breakpoints.mediumUp}>
            <Row isNested>
              {filteredPlans.map((plan: PlanDetails) => (
                <Col key={plan.id} small={6} medium={6} large={6}>
                  <ChargeAnytimePlans
                    plan={plan}
                    ctaText={'Select plan'}
                    onCtaClick={
                      switchMutation.isLoading ? undefined : handleSelectPlan
                    }
                    data-testid={`plan-card-${plan.id}`}
                    showMultipleEvsBadge={showMultipleEvsBadge(plan)}
                    variant={'choose-plan'}
                  />
                </Col>
              ))}
            </Row>
            <Row justifyContent="start">
              <Col small={1}>
                <Button
                  iconLeft="chevron-left"
                  variant="outline"
                  onClick={handleBack}
                  style={{ fontWeight: 800 }}
                >
                  Back
                </Button>
              </Col>
            </Row>
          </Media>
          <Media query={breakpoints.mediumDown}>
            <HorizontalScrollContainer>
              {filteredPlans.map((plan: PlanDetails) => (
                <ChargeAnytimePlans
                  plan={plan}
                  key={plan.id}
                  ctaText={'Select plan'}
                  onCtaClick={
                    switchMutation.isLoading ? undefined : handleSelectPlan
                  }
                  data-testid={`plan-card-${plan.id}`}
                  showMultipleEvsBadge={showMultipleEvsBadge(plan)}
                  variant={'choose-plan'}
                />
              ))}
            </HorizontalScrollContainer>
          </Media>
          {shouldShowFootnote && (
            <Margin vertical={8}>
              <Footnote productType={effectiveTab} />
            </Margin>
          )}
        </StyledContentGrid>
      </FullWidthContentWrapper>
    );
  };

  if (switchMutation.isLoading || isLoading) {
    return <Loading title={'Switch in progress'} />;
  }

  if (isError) {
    return <TechnicalGlitchPage header={'Check your set-up'} />;
  }

  if (eligibleSwitchProducts && eligibleSwitchProducts.length === 0) {
    return <TechnicalGlitchPage header={'Check your set-up'} />;
  }

  const introText =
    hasPayg && isOemMonthlyPlan ? (
      <>
        <Strong>Driving more than usual?</Strong>
        <P style={{ margin: '15px 0' }}>
          Our partner plan is just the thing when you're racking up the miles.
        </P>
      </>
    ) : hasPayg ? (
      <>
        <Strong>Driving more than usual?</Strong>
        <P style={{ margin: '15px 0' }}>
          Our Pay monthly plans are just the thing when you're racking up the
          miles – plus, they come with great perks.
        </P>
      </>
    ) : (
      <>
        <P>
          Switch to the perfect plan for you and drive as much – or as little –
          as you want. Your new plan will kick in at the start of the month.
        </P>
        <P style={{ margin: '10px 0' }}>
          It's always easy to switch back if your driving habits change.
        </P>
      </>
    );

  const planGridToRender =
    effectiveTab === 'payg'
      ? renderPlanGrid(paygPlans)
      : renderPlanGrid(monthlyPlans);

  return (
    <PageWithNavigation title="EV Charge Anytime - Eligibility">
      <PageHeaderV2 title="Make the switch" testId="switch-page-header" />
      <Margin top={8} />
      <MainContentCard>
        <HeaderRow style={isOemMonthlyPlan ? { paddingBottom: 0 } : undefined}>
          <StyledContentGrid>
            <Row>
              <Col medium={showTabs ? 7 : 12}>
                {introText}
                {effectiveTab === 'monthly' && !isOemMonthlyPlan && (
                  <P>
                    <StyledTextLink onClick={openMonthlyModal}>
                      Pay monthly perks in detail
                    </StyledTextLink>
                  </P>
                )}
                {effectiveTab === 'payg' && (
                  <P>
                    <StyledTextLink onClick={openPaygModal}>
                      Pay as you go details
                    </StyledTextLink>
                  </P>
                )}
              </Col>
              {showTabs && (
                <Col
                  medium={5}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <TabButtonGroup>
                    <TabButton
                      isActive={activeTab === 'monthly'}
                      onClick={() => handleTabChange('monthly')}
                    >
                      Pay monthly
                    </TabButton>
                    <TabButton
                      isActive={activeTab === 'payg'}
                      onClick={() => handleTabChange('payg')}
                    >
                      Pay as you go
                    </TabButton>
                  </TabButtonGroup>
                </Col>
              )}
            </Row>
          </StyledContentGrid>
        </HeaderRow>

        {planGridToRender}

        <MonthlyPlanModal
          isOpen={showMonthlyModal}
          onClose={closeMonthlyModal}
          currentTab={monthlyModalTab}
          currentStep={monthlyModalStep}
          onTabSwitch={handleTabSwitch}
          onNextStep={handleNextStep}
          onPrevStep={handlePrevStep}
        />
        <PayAsYouGoModal
          isOpen={showPaygModal}
          onClose={closePaygModal}
          planDetails={paygPlan}
        />
      </MainContentCard>
    </PageWithNavigation>
  );
};
